import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiGet } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Input } from '@/components/ui/Input';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pill } from '@/components/ui/Pill';
import { ErrorState } from '@/components/ui/States';
import { DataTable } from '@/components/ui/Table';
import type { CustomerRow, Paginated } from '@/types/api';
import { fmtPhone } from '@/utils/format';
import { useDebounced } from '@/utils/useDebounced';

/**
 * Customers list with overdue-first sort hint. The backend already
 * orders by `due_amount desc` when `?sort=overdue` is passed, so a CA
 * can immediately spot who to chase.
 */
export function CustomersListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const query = useQuery({
    queryKey: ['customers', { search: debouncedSearch, overdueOnly }],
    queryFn: () =>
      apiGet<Paginated<CustomerRow> | CustomerRow[]>(endpoints.customers, {
        search: debouncedSearch || undefined,
        sort: overdueOnly ? 'overdue' : undefined,
      }),
  });

  const rows: CustomerRow[] = Array.isArray(query.data)
    ? query.data
    : query.data?.items ?? [];

  const filtered = overdueOnly ? rows.filter((c) => (c.due_amount ?? 0) > 0) : rows;

  return (
    <>
      <PageHeader
        title="Customers"
        description="Everyone you've billed, with their dues at a glance."
      />

      <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-3 mb-4">
        <Input
          name="search"
          placeholder="Search by name or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          iconLeft={<Search className="h-4 w-4" />}
        />
        <label className="flex items-center gap-2 h-10 px-3 rounded-xl border border-line bg-surface text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
            className="h-4 w-4 accent-brand-600"
          />
          <span>Show overdue only</span>
        </label>
      </div>

      {query.isError ? (
        <ErrorState
          title="Couldn't load customers"
          description={(query.error as Error)?.message}
          onRetry={() => query.refetch()}
        />
      ) : (
        <DataTable
          rowKey={(c) => c.id}
          loading={query.isLoading}
          rows={filtered}
          onRowClick={(c) => navigate(`/customers/${c.id}`)}
          empty={
            debouncedSearch
              ? 'No customers match that search.'
              : 'No customers yet.'
          }
          columns={[
            {
              key: 'name',
              header: 'Name',
              cell: (c) => (
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-brand-500/10 text-brand-700 grid place-items-center font-semibold text-sm">
                    {(c.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-xs text-ink-soft">{fmtPhone(c.phone)}</div>
                  </div>
                </div>
              ),
            },
            {
              key: 'tags',
              header: 'Tags',
              hideOnMobile: true,
              cell: (c) =>
                (c.tags ?? []).length === 0 ? (
                  <span className="text-ink-soft text-xs">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {(c.tags ?? []).slice(0, 3).map((t) => (
                      <Pill key={t} tone="brand">
                        {t}
                      </Pill>
                    ))}
                  </div>
                ),
            },
            {
              key: 'bills',
              header: 'Bills',
              className: 'text-right',
              hideOnMobile: true,
              cell: (c) => c.bills_count ?? 0,
            },
            {
              key: 'spent',
              header: 'Total spent',
              className: 'text-right',
              hideOnMobile: true,
              cell: (c) => <Money value={c.total_spent ?? 0} />,
            },
            {
              key: 'due',
              header: 'Due',
              className: 'text-right',
              cell: (c) => {
                const due = c.due_amount ?? 0;
                return (
                  <Money
                    value={due}
                    tone={due > 0 ? 'negative' : 'neutral'}
                    className="font-semibold"
                  />
                );
              },
            },
          ]}
        />
      )}
    </>
  );
}
